/*
 * Discount cookie promotions.
 *
 * Reads the discount code Shopify stores in the visitor's discount cookie, asks
 * the theme whether it matches one of the codes configured in
 * Theme settings > Discount cookie promotions, and if it does shows the
 * discounted price and the promo message on the product cards, product pages
 * and cart that the code applies to.
 *
 * A code can be mapped to product handles, collections and SKUs; a code with no
 * mapping applies to every product.
 *
 * The configured codes/discounts/messages/mappings never reach the storefront
 * HTML: the lookup happens in templates/search.discount-cookie.liquid and only
 * the rule matching the visitor's own code is returned.
 */
(function () {
  'use strict';

  var settings = window.discountCookieSettings;
  if (!settings) return;

  var CACHE_PREFIX = 'discount-cookie:v2:';
  var rule = null;
  var targets = null;
  var scheduled = false;
  var observer = null;
  // Add ?discount_cookie_debug=1 to any storefront URL to trace what the
  // promotion did or did not find, or set window.discountCookieDebug = true.
  var debugging =
    window.discountCookieDebug === true ||
    window.location.search.indexOf('discount_cookie_debug') !== -1;

  function debug() {
    if (!debugging || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[discount-cookie]');
    window.console.log.apply(window.console, args);
  }

  /* ------------------------------------------------------------------ money */

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var number = (cents / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? dollars + decimal + parts[1] : dollars;
  }

  function formatMoney(cents) {
    var format = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat || settings.moneyFormat
      : settings.moneyFormat;
    if (!format) format = '${{amount}}';

    var placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var amount;

    switch (placeholder && placeholder[1]) {
      case 'amount_no_decimals':
        amount = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        amount = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_period_and_space_separator':
        amount = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        amount = formatWithDelimiters(cents, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  /* ------------------------------------------------------------------- code */

  function readCookie(name) {
    var escaped = name.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1');
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
    if (!match) return '';

    // Shopify form encodes the discount cookie: /discount/Second%20Chance%20Pump
    // is stored as discount_code=Second+Chance+Pump, and a code containing a
    // real plus is stored as %2B. Decode plus signs first, exactly as a form
    // decoder does, so both come back as the code the merchant sees in admin.
    var value = match[1].replace(/\+/g, ' ');
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  function detectCode() {
    var fromUrl = new URLSearchParams(window.location.search).get('discount');
    if (fromUrl && fromUrl.trim()) {
      debug('code from the discount URL parameter:', fromUrl.trim());
      return fromUrl.trim();
    }

    var names = settings.cookieNames || [];
    debug('looking for cookies', names, '| document.cookie:', document.cookie);
    for (var index = 0; index < names.length; index++) {
      var name = (names[index] || '').trim();
      if (!name) continue;
      var value = readCookie(name).trim();
      if (value) {
        debug('code from the ' + name + ' cookie:', value);
        return value;
      }
    }

    // Falls back to the code already applied to the cart, which covers visitors
    // whose discount cookie cannot be read from JavaScript.
    var fromCart = (settings.cartDiscountCode || '').trim();
    debug(fromCart ? 'code from the cart discount:' : 'no code found', fromCart);
    return fromCart;
  }

  /* ------------------------------------------------------------------ lookup */

  function cacheKey(code) {
    return CACHE_PREFIX + code.toUpperCase();
  }

  function readCache(code) {
    try {
      var cached = sessionStorage.getItem(cacheKey(code));
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(code, response) {
    try {
      sessionStorage.setItem(cacheKey(code), JSON.stringify(response));
    } catch (error) {
      /* Private browsing or a full quota — the lookup just runs again. */
    }
  }

  function lookup(code) {
    var cached = readCache(code);
    if (cached) return Promise.resolve(cached);

    var url =
      settings.endpoint +
      (settings.endpoint.indexOf('?') === -1 ? '?' : '&') +
      'q=' +
      encodeURIComponent(code) +
      '&view=' +
      encodeURIComponent(settings.view || 'discount-cookie');

    debug('looking the code up at', url);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Discount lookup failed');
        return response.json();
      })
      .then(function (response) {
        writeCache(code, response);
        return response;
      })
      .catch(function (error) {
        debug('lookup failed', error);
        return { match: false };
      });
  }

  /* -------------------------------------------------------------- targeting */

  function normalize(values, upperCase) {
    var normalized = [];
    (values || []).forEach(function (value) {
      var cleaned = String(value == null ? '' : value).trim();
      if (!cleaned) return;
      normalized.push(upperCase ? cleaned.toUpperCase() : cleaned.toLowerCase());
    });
    return normalized;
  }

  function buildTargets(response) {
    var built = {
      products: normalize(response.products, false),
      collections: normalize(response.collections, false),
      skus: normalize(response.skus, true)
    };
    // A rule with no targets applies to the whole catalogue.
    built.scoped =
      built.products.length + built.collections.length + built.skus.length > 0;
    return built;
  }

  function overlaps(values, wanted) {
    if (!wanted.length) return false;
    for (var index = 0; index < values.length; index++) {
      if (wanted.indexOf(values[index]) !== -1) return true;
    }
    return false;
  }

  function attributeList(element, attribute, upperCase) {
    var raw = element.getAttribute(attribute);
    if (!raw) return [];
    return normalize(raw.split(','), upperCase);
  }

  // Product prices carry their own handle, collections and SKUs; the cart
  // message carries those of everything in the cart.
  function isTargeted(element) {
    if (!targets.scoped) return true;
    return (
      overlaps(attributeList(element, 'data-discount-cookie-products', false), targets.products) ||
      overlaps(attributeList(element, 'data-discount-cookie-collections', false), targets.collections) ||
      overlaps(attributeList(element, 'data-discount-cookie-skus', true), targets.skus)
    );
  }

  /* ------------------------------------------------------------------- view */

  function escapeHtml(value) {
    var element = document.createElement('span');
    element.textContent = value == null ? '' : value;
    return element.innerHTML;
  }

  function buildMessage() {
    var message = document.createElement('div');
    message.className = 'discount-cookie-message';
    message.textContent = rule.message;
    return message;
  }

  // Shopify rounds a percentage discount DOWN to the cent and subtracts it, so
  // 30% off $179.99 is a $53.99 discount and a $126.00 price, not the $125.99 a
  // rounded 70% of the price would give. Matching that keeps what is shown on a
  // product from drifting a cent away from what the cart charges.
  //
  // The rate is converted to basis points so the arithmetic stays on exact
  // integers: 0.3 * 17999 is 5399.699999999999 in floating point, and a rate
  // that should land on a whole cent could otherwise floor to a cent short.
  function discountedPrice(cents) {
    var basisPoints = Math.round(rule.rate * 10000);
    var discount = Math.floor((cents * basisPoints) / 10000);
    return cents - discount;
  }

  function renderDiscountedPrice(priceElement, cents) {
    var container = priceElement.querySelector('.price__container');
    if (!container) return;

    var discounted = discountedPrice(cents);
    if (!(discounted > 0) || discounted >= cents) return;

    var labels = container.querySelectorAll('.price__sale > .visually-hidden');
    var regularLabel = labels[0] ? labels[0].textContent.trim() : '';
    var saleLabel = labels[1] ? labels[1].textContent.trim() : '';

    var sale = document.createElement('div');
    sale.className = 'price__sale discount-cookie-price__sale';
    sale.innerHTML =
      '<span class="visually-hidden visually-hidden--inline">' +
      escapeHtml(regularLabel) +
      '</span>' +
      '<span><s class="price-item price-item--regular">' +
      formatMoney(cents) +
      '</s></span>' +
      '<span class="visually-hidden visually-hidden--inline">' +
      escapeHtml(saleLabel) +
      '</span>' +
      '<span class="price-item price-item--sale price-item--last">' +
      formatMoney(discounted) +
      '</span>';

    var unitPrice = container.querySelector('.unit-price');
    if (unitPrice) {
      container.insertBefore(sale, unitPrice);
    } else {
      container.appendChild(sale);
    }

    priceElement.classList.add('price--on-sale', 'price--discount-cookie');
  }

  function applyToProducts() {
    if (!settings.showOnProducts) return;

    // Everything below is checked against what is already in the DOM rather
    // than against a "done" flag, so prices re-rendered by the theme (variant
    // changes, filtering, pagination) get the promotion back.
    document.querySelectorAll('.price[data-discount-cookie-price]').forEach(function (priceElement) {
      if (!isTargeted(priceElement)) return;

      var cents = parseInt(priceElement.getAttribute('data-discount-cookie-price'), 10);
      var varies = priceElement.getAttribute('data-discount-cookie-varies') === 'true';

      if (
        settings.showPrice &&
        !varies &&
        cents > 0 &&
        !priceElement.querySelector('.discount-cookie-price__sale')
      ) {
        renderDiscountedPrice(priceElement, cents);
      }

      if (rule.message) {
        var next = priceElement.nextElementSibling;
        if (!next || !next.classList.contains('discount-cookie-message')) {
          priceElement.insertAdjacentElement('afterend', buildMessage());
        }
      }
    });
  }

  function applyToCart() {
    if (!settings.showOnCart || !rule.message) return;

    document.querySelectorAll('[data-discount-cookie-cart-message]').forEach(function (placeholder) {
      if (!placeholder.hidden || !isTargeted(placeholder)) return;
      placeholder.textContent = rule.message;
      placeholder.hidden = false;
    });
  }

  function apply() {
    scheduled = false;
    applyToProducts();
    applyToCart();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(apply);
  }

  function watch() {
    if (observer || !window.MutationObserver) return;
    // Prices and cart contents are re-rendered on variant changes, filtering,
    // pagination and cart updates, so re-apply whenever the DOM changes.
    observer = new MutationObserver(schedule);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------- boot */

  // Debug only: says what the promotion matched, and why it matched nothing.
  function report() {
    if (!debugging) return;

    var prices = document.querySelectorAll('.price[data-discount-cookie-price]');
    var shown = document.querySelectorAll('.discount-cookie-message').length;
    debug(
      'showing on ' + shown + ' of ' + prices.length + ' price element(s);',
      targets.scoped ? 'this code is limited to' : 'this code applies to every product',
      targets.scoped ? targets : ''
    );

    if (shown || !prices.length || !targets.scoped) return;
    debug(
      'nothing matched. Compare the targets above with what this page offers, ' +
        'for example this product:',
      {
        products: prices[0].getAttribute('data-discount-cookie-products'),
        collections: prices[0].getAttribute('data-discount-cookie-collections'),
        skus: prices[0].getAttribute('data-discount-cookie-skus')
      }
    );
  }

  function start() {
    var code = detectCode();
    if (!code) return;
    debug('settings', settings);

    lookup(code).then(function (response) {
      debug('lookup result', response);
      if (!response || !response.match) {
        debug('the code is not one of the configured discounts');
        return;
      }
      rule = response;
      targets = buildTargets(response);
      window.discountCookie = {
        code: response.code,
        rate: response.rate,
        message: response.message,
        targets: targets
      };
      apply();
      watch();
      report();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
